import { generatePath } from 'react-router';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { PdfReport, PaginationResponse } from 'types';
import { providesList, invalidatesList } from 'utils/query-cache';

import { apiQuery } from '../../query';

import {
  PdfReportRequest,
  PdfReportListRequest,
  PdfReportDownloadUrl,
  PdfReportListResponse,
  PdfReportCreateRequest,
  PdfReportDetailResponse,
  PdfReportDownloadUrlResponse,
} from './types';

const tagType = 'PDF_REPORTS' as const;

export const pdfReportsApi = createApi({
  tagTypes: [tagType],
  baseQuery: apiQuery,
  reducerPath: 'pdfReportsApi',
  endpoints: (build) => ({
    restore: build.mutation<{ success: boolean }, PdfReportRequest>({
      invalidatesTags: invalidatesList(tagType),
      query: ({ userId, reportId }) => ({
        method: 'post',
        url: generatePath(URL.USER_PDF_REPORT_RESTORE, { userId, reportId }),
      }),
    }),

    delete: build.mutation<{ success: boolean }, PdfReportRequest>({
      invalidatesTags: invalidatesList(tagType),
      query: ({ userId, reportId }) => ({
        method: 'delete',
        url: generatePath(URL.USER_PDF_REPORT_DETAILS, { userId, reportId }),
      }),
    }),

    getDownloadUrl: build.query<PdfReportDownloadUrl, PdfReportRequest>({
      transformResponse: (response: PdfReportDownloadUrlResponse) =>
        response.data,
      query: ({ userId, reportId }) => ({
        method: 'get',
        url: generatePath(URL.USER_PDF_REPORT_DOWNLOAD_URL, {
          userId,
          reportId,
        }),
      }),
    }),

    list: build.query<PaginationResponse<PdfReport>, PdfReportListRequest>({
      providesTags: providesList(tagType),
      transformResponse: (response: PdfReportListResponse) => response.data,
      query: ({ userId, ...params }) => ({
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.USER_PDF_REPORTS, { userId }),
          params
        ),
      }),
    }),

    create: build.mutation<PdfReport, PdfReportCreateRequest>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: PdfReportDetailResponse) => response.data,
      query: ({ data, userId, tenantId }) => ({
        data,
        method: 'post',
        url: addParamsToUrl(
          generatePath(URL.USER_PDF_REPORTS, { userId }),
          tenantId ? { tenantId } : {}
        ),
      }),
    }),
  }),
});

export const {
  useLazyListQuery,
  useCreateMutation,
  useDeleteMutation,
  useRestoreMutation,
  useLazyGetDownloadUrlQuery,
} = pdfReportsApi;

export type { PdfReportListRequest };
